import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './SharePost.css';
import AppStoreButton from './AppStoreButton';

export default function SharePost() {
  const { postId } = useParams();
  const [postData, setPostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch post data from Supabase Edge Function
    // The postId in the URL is the share token/UUID generated when sharing
    const fetchPost = async () => {
      try {
        setLoading(true);
        
        // Validate postId is a valid UUID format
        const uuidRegex = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
        if (!uuidRegex.test(postId)) {
          throw new Error('Invalid post ID format');
        }
        
        const SUPABASE_FUNCTIONS_URL = process.env.REACT_APP_SUPABASE_FUNCTIONS_URL || 'https://lyyjnlgqwfskbvwqqlrh.functions.supabase.co';
        const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
        
        if (!SUPABASE_ANON_KEY) {
          throw new Error('API configuration error');
        }
        
        // Call the Supabase Edge Function that returns post data with full signed URL
        const response = await fetch(
          `${SUPABASE_FUNCTIONS_URL}/get-post-preview?id=${encodeURIComponent(postId)}`,
          {
            headers: {
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!response.ok) {
          // Don't expose HTTP status codes to users
          throw new Error('Failed to load post');
        }
        
        const post = await response.json();
        
        if (post && post.id) {
          // The Edge Function returns the post with full signed URL already included
          // Only log in development
          if (process.env.NODE_ENV === 'development') {
            console.log('Post data from Edge Function:', post);
          }
          
          setPostData({
            id: post.id,
            username: post.username || 'Unknown',
            caption: post.caption || '',
            image_url: post.image_url || '', // Full signed URL from Edge Function
            expires_in_seconds: post.expires_in_seconds || 604800
          });
        } else {
          setError('Post not found. The share link may have expired or been deleted.');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching post:', err);
        setError('Failed to load post. The post may have expired or been deleted.');
        setLoading(false);
      }
    };

    if (postId) {
      fetchPost();
    } else {
      setError('Invalid share link');
      setLoading(false);
    }
  }, [postId]);

  // Update meta tags for social sharing
  useEffect(() => {
    if (postData) {
      // Update Open Graph tags dynamically
      const updateMetaTag = (property, content) => {
        let element = document.querySelector(`meta[property="${property}"]`);
        if (!element) {
          element = document.createElement('meta');
          element.setAttribute('property', property);
          document.head.appendChild(element);
        }
        element.setAttribute('content', content);
      };

      const updateNameTag = (name, content) => {
        let element = document.querySelector(`meta[name="${name}"]`);
        if (!element) {
          element = document.createElement('meta');
          element.setAttribute('name', name);
          document.head.appendChild(element);
        }
        element.setAttribute('content', content);
      };

      const postUrl = `https://www.yuppy.nyc/posts/${postId}`;
      const title = `Check out ${postData.username}'s latest post on Yuppy`;
      const description = postData.caption || "View the post and join NYC's top dog community.";

      // Update title
      document.title = title;

      // Update Open Graph tags
      updateMetaTag('og:title', title);
      updateMetaTag('og:description', description);
      updateMetaTag('og:image', postData.image_url);
      updateMetaTag('og:url', postUrl);
      updateMetaTag('og:type', 'article');

      // Update Twitter tags
      updateNameTag('twitter:card', 'summary_large_image');
      updateNameTag('twitter:title', title);
      updateNameTag('twitter:description', description);
      updateNameTag('twitter:image', postData.image_url);
    }
  }, [postData, postId]);

  const handleOpenApp = () => {
    // Try to open the app using universal link
    // If app is installed, it will open; otherwise, it will fall back to App Store
    const appUrl = `yuppy://posts/${postId}`;
    const appStoreUrl = 'https://apps.apple.com/us/app/yuppy-nyc/id6749724524';
    
    // Try to open app first
    window.location.href = appUrl;
    
    // Fallback to App Store after a short delay if app doesn't open
    setTimeout(() => {
      window.location.href = appStoreUrl;
    }, 500);
  };

  if (loading) {
    return (
      <div className="share-post-container">
        <div className="share-post-loading">Loading...</div>
      </div>
    );
  }

  if (error || !postData) {
    return (
      <div className="share-post-container">
        <div className="share-post-error">
          <h2>Post Not Found</h2>
          <p>{error || 'This post could not be found.'}</p>
          <AppStoreButton />
        </div>
      </div>
    );
  }

  return (
    <div className="share-post-container">
      {/* Mobile-only header */}
      <div className="share-post-mobile-header">
        <div className="share-post-header-logo">
          <img 
            src={require('./assets/images/fixed-logo-1024.png')} 
            alt="Yuppy logo" 
            className="share-post-logo-icon"
          />
          <span className="share-post-header-text">yuppy</span>
        </div>
      </div>
      <div className="share-post-card">
        <div className="share-post-image-container">
          {postData.image_url ? (
            <img 
              src={postData.image_url} 
              alt={`${postData.username}'s dog post on Yuppy`}
              className="share-post-image"
              onError={(e) => {
                // Only log in development
                if (process.env.NODE_ENV === 'development') {
                  console.error('Image failed to load:', postData.image_url);
                }
                e.target.src = require('./assets/images/fixed-logo-1024.png');
              }}
              onLoad={() => {
                // Only log in development
                if (process.env.NODE_ENV === 'development') {
                  console.log('Image loaded successfully');
                }
              }}
            />
          ) : (
            <div className="share-post-image-placeholder">
              <img 
                src={require('./assets/images/fixed-logo-1024.png')} 
                alt="Yuppy logo"
                className="share-post-image"
              />
            </div>
          )}
        </div>
        <div className="share-post-content">
          <h2 className="share-post-title">
            Check out {postData.username}'s latest post on Yuppy
          </h2>
          <p className="share-post-description">
            {postData.caption || "View the post and join NYC's top dog community."}
          </p>
          <div className="share-post-cta">
            <button 
              className="share-post-open-button"
              onClick={handleOpenApp}
            >
              Open Yuppy
            </button>
            <a 
              className="share-post-download-button"
              href="https://apps.apple.com/us/app/yuppy-nyc/id6749724524"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Yuppy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

